import { DataSet, ColumnId } from '../../types';
import { TimeSeriesRangeConfig } from '../../types/graph-builder.types';
import { useD3TimeSeriesChart } from '../hooks/useD3TimeSeriesChart';
import * as styles from '../styles/components/time-series.css.ts';

interface MultiSeriesTimeSeriesPlotProps {
  dataset: DataSet;
  dateColumn: ColumnId;
  valueColumns: ColumnId[];
  rangeColumns?: TimeSeriesRangeConfig[];
  rangeOpacity?: number;
  filteredRowIndices?: number[] | null;
  selectedRows?: number[];
  width?: number;
  height?: number;
  onPointClick?: (dataIndices: number[], isAdditive?: boolean) => void;
}

/**
 * MultiSeriesTimeSeriesPlot - 複数系列の時系列データを表示するコンポーネント
 * 
 * 機能:
 * - 日時型列と複数のnumeric型列から複数系列の時系列グラフを生成
 * - D3.jsを使ったSVGベースの可視化
 * - ズーム・パン機能
 * - ポイントクリックによる行選択
 * - 選択状態の可視化
 * - 系列ごとの色分け
 * - 凡例表示
 * 
 * 操作方法:
 * - マウスホイール: ズームイン/アウト
 * - ドラッグ: パン（移動）
 * - ポイントクリック: 対応する行の選択
 * - Cmd/Ctrl+ポイントクリック: 複数選択
 */
export function MultiSeriesTimeSeriesPlot({
  dataset,
  dateColumn,
  valueColumns,
  rangeColumns,
  rangeOpacity,
  filteredRowIndices,
  selectedRows = [],
  width = 300,
  height = 200,
  onPointClick
}: MultiSeriesTimeSeriesPlotProps) {
  // カスタムフックを使用（初期値は渡すが、実際のサイズはResizeObserverで取得）
  const { svgRef, containerRef, actualWidth, actualHeight } = useD3TimeSeriesChart({
    dataset,
    dateColumn,
    valueColumns,
    rangeColumns,
    rangeOpacity,
    filteredRowIndices,
    selectedRows,
    width,
    height,
    onPointClick
  });

  // データがない場合のメッセージ（Y軸カラムも範囲もない場合）
  const hasRanges = rangeColumns &&
    rangeColumns.some(r => r.upperColumn && r.lowerColumn);

  if (!dataset || (valueColumns.length === 0 && !hasRanges)) {
    return (
      <div className={styles.emptyState} style={{ width: '100%', height: '100%' }}>
        データがありません
      </div>
    );
  }

  return (
    <div ref={containerRef} className={styles.timeSeriesContainer} style={{ width: '100%', height: '100%' }}>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <svg
          ref={svgRef}
          width={actualWidth}
          height={actualHeight}
          className={styles.timeSeriesSvg}
        />
        
        {/* パンモードインジケーター */}
        <div style={{ 
          position: 'absolute',
          top: '8px',
          right: '8px',
          display: 'flex',
          gap: '4px',
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '4px',
          padding: '2px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          pointerEvents: 'none'
        }}>
          <div
            title="パンモード"
            style={{
              width: '28px',
              height: '28px',
              padding: '0',
              border: 'none',
              borderRadius: '4px',
              background: 'rgba(66, 133, 244, 0.2)',
              cursor: 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto'
            }}
          >
            {/* パンアイコン（移動矢印） */}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1L6 3h1.5v4.5H3V6L1 8l2 2v-1.5h4.5V13H6l2 2 2-2H8.5V8.5H13V10l2-2-2-2v1.5H8.5V3H10L8 1z" 
                fill="#4285f4" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}